import React, { CSSProperties, memo, useCallback, useMemo } from 'react';
import { IdType, ILabelType } from '../../../../imports/annotation-canvas';
import {
  LabelButtonItemView,
  RectangleToolIcon
} from '../../../../imports/ui-parts';
import {
  higherContrastTextColorHsl,
  Hsl,
  hslToStr,
  Percent
} from '../../../../imports/utils';

export const LabelButtonItem = memo(
  ({
    index,
    label,
    labelSaturation,
    labelLightness,
    onLabelClick: onLabelClickInput,
    onVisibilityClick,
    isSelectedLabel,
    isVisibleLabel,
    disabled
  }: Readonly<{
    index: number;
    label: ILabelType;
    labelSaturation: Percent;
    labelLightness: Percent;
    isSelectedLabel: boolean;
    isVisibleLabel: boolean;
    onLabelClick: (id: IdType) => void;
    onVisibilityClick: (id: IdType) => void;
    disabled: boolean;
  }>) => {
    const hsl = useMemo<Hsl>(
      () => [label.hue, labelSaturation, labelLightness],
      [label.hue, labelSaturation, labelLightness]
    );

    const textColor = useMemo(() => higherContrastTextColorHsl(hsl), [hsl]);

    const labelButtonColors = useMemo<CSSProperties>(
      () => ({
        backgroundColor: hslToStr(hsl),
        color: textColor
      }),
      [hsl, textColor]
    );

    const annotationModeIcon = useMemo<JSX.Element>(
      () => <RectangleToolIcon style={{ color: textColor }} />,
      [textColor]
    );

    const onLabelClick = useCallback(() => {
      onLabelClickInput(label.id);
    }, [onLabelClickInput, label.id]);

    const onVisibilityIconClick = useCallback(
      (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        onVisibilityClick(label.id);
        ev.stopPropagation();
      },
      [onVisibilityClick, label.id]
    );

    return (
      <LabelButtonItemView
        index={index}
        labelButtonColors={labelButtonColors}
        annotationModeIcon={annotationModeIcon}
        labelText={label.name}
        isSelectedLabel={isSelectedLabel}
        isVisibleLabel={isVisibleLabel}
        onLabelClick={onLabelClick}
        onVisibilityIconClick={onVisibilityIconClick}
        disabled={disabled}
      />
    );
  }
);

LabelButtonItem.displayName = 'LabelButtonItem';
